import { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { GetAiAgentDropdownsOptions } from '@/apis/settings';
import { useSpin } from '@/components/SpinContext';
import { useUnAuthorize } from '@/hooks';

import type {
  ApiCollect,
  ApiEscalation,
  ApiPurpose,
  CollectOptionValue,
  EscalateWhenValue,
  PurposeOptionValue,
} from '../types';
import {
  toCustomVariableTypeName,
  type CustomVariableTypeName,
} from '@/common/collectVariableType';

type ApiOption<T extends string = string> = {
  value: T;
  label: string;
  type?: CustomVariableTypeName;
  isCustomVar?: boolean;
  description?: string;
  sample_value?: string;
  is_in_use?: boolean;
  // Not yet persisted as an AIPCACustomVar row - created (and linked to the
  // agent) only when the agent itself is saved. See onCreateCustomVar in
  // SettingsAgentDrawerForm.tsx.
  isPending?: boolean;
};

type DropdownApiResponse = {
  data?: {
    data?: {
      is_success?: boolean;
      message?: string;
      data?: {
        collect?: ApiCollect[];
        escalation?: ApiEscalation[];
        purpose?: ApiPurpose[];
      };
    };
  };
};

export const useAgentDropdowns = ({
  drawerOpen,
  token,
}: {
  drawerOpen: boolean;
  token?: string | null;
}) => {
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();

  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const [collectOptions, setCollectOptions] = useState<
    ApiOption<CollectOptionValue>[]
  >([]);
  const [escalateWhenOptions, setEscalateWhenOptions] = useState<
    ApiOption<EscalateWhenValue>[]
  >([]);
  const [purposeOptions, setPurposeOptions] = useState<
    Array<{
      value: PurposeOptionValue;
      label: string;
      direction: ApiPurpose['direction'];
    }>
  >([]);

  const [rawCollectData, setRawCollectData] = useState<ApiCollect[]>([]);
  const [rawEscalationData, setRawEscalationData] = useState<ApiEscalation[]>(
    []
  );

  const dropdownsReady = useMemo(() => {
    if (loadingDropdowns) return false;
    if (!drawerOpen || !token) return false;
    return (
      purposeOptions.length > 0 &&
      collectOptions.length > 0 &&
      escalateWhenOptions.length > 0
    );
  }, [
    loadingDropdowns,
    drawerOpen,
    token,
    purposeOptions.length,
    collectOptions.length,
    escalateWhenOptions.length,
  ]);

  useEffect(() => {
    if (!drawerOpen || !token) return;

    setLoadingDropdowns(true);
    showSpin();

    GetAiAgentDropdownsOptions(token)
      .then((res) => {
        const meta = (res as DropdownApiResponse)?.data?.data;

        if (meta?.is_success === false) {
          message.error(meta?.message || 'Failed to load dropdown options');
          return;
        }

        const dropdownData = meta?.data ?? {};

        const collectData = dropdownData.collect ?? [];
        const escalationData = dropdownData.escalation ?? [];
        const purposeData = dropdownData.purpose ?? [];

        setRawCollectData(collectData.filter((c) => !c.is_archived));
        setRawEscalationData(escalationData.filter((e) => !e.is_archived));

        setCollectOptions(
          collectData
            .filter((c) => !c.is_archived)
            .map((c) => ({
              value: String(c.id) as CollectOptionValue,
              label: c.display_name,
              type: toCustomVariableTypeName(c.variable_type),
              isCustomVar: c.isCustomVar,
              description: c.description ?? undefined,
              sample_value: c.sample_value ?? undefined,
              is_in_use: c.is_in_use,
            }))
        );

        setEscalateWhenOptions(
          escalationData
            .filter((e) => !e.is_archived)
            .map((e) => ({
              value: String(e.id) as EscalateWhenValue,
              label: e.name,
            }))
        );

        setPurposeOptions(
          purposeData
            .filter((p) => !p.is_archived)
            .map((p) => ({
              value: String(p.id) as PurposeOptionValue,
              label: p.name,
              direction: p.direction,
            }))
        );
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Failed to load dropdown options');
      })
      .finally(() => {
        setLoadingDropdowns(false);
        hideSpin();
      });
  }, [drawerOpen, token, showSpin, hideSpin, unauthorize]);

  return {
    loadingDropdowns,
    dropdownsReady,
    collectOptions,
    setCollectOptions,
    escalateWhenOptions,
    purposeOptions,
    rawCollectData,
    rawEscalationData,
  };
};
